import { useState } from "react";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Stack,
  Heading,
  Text,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Button,
  Link,
  useToast,
} from "@chakra-ui/react";
import { resetPasswordRequestSchema, resetPasswordConfirmSchema } from "../schemas/authSchemas";
import { useResetPasswordRequestMutation } from "../hooks/useResetPasswordRequestMutation";
import { useResetPasswordConfirmMutation } from "../hooks/useResetPasswordConfirmMutation";

// O e-mail de reset gerado pelo backend aponta para /reset-password?token=... — esta
// página decide, com base na presença de ?token=, se mostra o formulário de solicitação
// ou o de confirmação com nova senha.
export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  return token ? <ConfirmarNovaSenha token={token} /> : <SolicitarReset />;
}

function SolicitarReset() {
  const toast = useToast();
  const [enviado, setEnviado] = useState(false);
  const mutation = useResetPasswordRequestMutation();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: zodResolver(resetPasswordRequestSchema), defaultValues: { email: "" } });

  if (enviado) {
    return (
      <Stack spacing={4} textAlign="center">
        <Heading size="md">Verifique seu e-mail</Heading>
        <Text color="app-muted-fg" fontSize="sm">
          Se o e-mail informado existir, enviamos um link para redefinir sua senha.
        </Text>
        <Link as={RouterLink} to="/auth/login" color="accent-text" fontSize="sm">
          Voltar para o login
        </Link>
      </Stack>
    );
  }

  const onSubmit = (data) => {
    mutation.mutate(data.email, {
      onSuccess: () => setEnviado(true),
      onError: () =>
        toast({ title: "Não foi possível processar o pedido", status: "error", duration: 5000, isClosable: true }),
    });
  };

  return (
    <Stack spacing={6}>
      <Stack spacing={1} textAlign="center">
        <Heading size="md">Redefinir senha</Heading>
        <Text color="app-muted-fg" fontSize="sm">
          Informe seu e-mail para receber o link de redefinição
        </Text>
      </Stack>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Stack spacing={4}>
          <FormControl isInvalid={Boolean(errors.email)}>
            <FormLabel htmlFor="email">E-mail</FormLabel>
            <Input id="email" type="email" autoFocus {...register("email")} />
            <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
          </FormControl>
          <Button type="submit" size="lg" isLoading={mutation.isPending}>
            Enviar link
          </Button>
        </Stack>
      </form>

      <Link as={RouterLink} to="/auth/login" color="accent-text" fontSize="sm" textAlign="center">
        Voltar para o login
      </Link>
    </Stack>
  );
}

function ConfirmarNovaSenha({ token }) {
  const navigate = useNavigate();
  const toast = useToast();
  const mutation = useResetPasswordConfirmMutation();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: zodResolver(resetPasswordConfirmSchema), defaultValues: { novaSenha: "", confirmarSenha: "" } });

  const onSubmit = (data) => {
    mutation.mutate(
      { token, newPassword: data.novaSenha },
      {
        onSuccess: () => {
          toast({ title: "Senha redefinida com sucesso", status: "success", duration: 4000 });
          navigate("/auth/login");
        },
        onError: (error) =>
          toast({
            title: "Não foi possível redefinir sua senha",
            description: error.response?.data?.mensagem || "O link pode ter expirado — solicite um novo.",
            status: "error",
            duration: 6000,
            isClosable: true,
          }),
      }
    );
  };

  return (
    <Stack spacing={6}>
      <Stack spacing={1} textAlign="center">
        <Heading size="md">Nova senha</Heading>
        <Text color="app-muted-fg" fontSize="sm">
          Escolha uma nova senha para sua conta
        </Text>
      </Stack>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Stack spacing={4}>
          <FormControl isInvalid={Boolean(errors.novaSenha)}>
            <FormLabel htmlFor="novaSenha">Nova senha</FormLabel>
            <Input id="novaSenha" type="password" autoFocus autoComplete="new-password" {...register("novaSenha")} />
            <FormErrorMessage>{errors.novaSenha?.message}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={Boolean(errors.confirmarSenha)}>
            <FormLabel htmlFor="confirmarSenha">Confirmar nova senha</FormLabel>
            <Input id="confirmarSenha" type="password" autoComplete="new-password" {...register("confirmarSenha")} />
            <FormErrorMessage>{errors.confirmarSenha?.message}</FormErrorMessage>
          </FormControl>
          <Button type="submit" size="lg" isLoading={mutation.isPending}>
            Redefinir senha
          </Button>
        </Stack>
      </form>
    </Stack>
  );
}
